import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from "next";
import React, { useMemo } from "react";
import ArticlesList from "../../../components/articles-list";
import Breadcrumbs from "../../../components/breadcrumbs";
import CategoriesList from "../../../components/categories-list";
import StandardLayout from "../../../components/layout.standard";
import loadCategories, { type Section } from "../../../lib/load-categories";
import loadCategoryArticles, {
  type Article,
} from "../../../lib/load-category-articles";
import styles from "../../../styles/general.module.css";
import type { AppSettings } from "../../../lib/get-app-settings";
import getBaseUrl from "../../../lib/get-base-url";

import {
  THCSessionDetails,
  getSessionDetailsFromRequest,
  saveSessionDetailsToLocalStorage,
} from "../../../lib/hc-session";
import trackSessionEventCategoryVisit from "../../../lib/hc-session";
import getPageUrl, { getDomain } from "../../../lib/get-page-url";
import getObjectByAlias from "../../../lib/get-object-by-alias";
import notFound from "../../../lib/not-found";
import { TCustomer } from "../../../models/customer";
import loadCustomer from "../../../lib/load-customer";
import t from "../../../texts/t";
import { THelpCenterStandardLayout } from "../../../lib/load-company-settings";
import getRedirectUrl from "../../../lib/get-redirect-url";

export type TCategoryStandardPageProps = {
  appSettings: AppSettings;
  category: Section;
  subcategories: Section[];
  parents: Section[];
  articles: Article[];
  hcSessionDetails: THCSessionDetails;
  customerData: TCustomer | null;
};

const CategoryStandardPage: NextPage<TCategoryStandardPageProps> = ({
  appSettings,
  category,
  subcategories,
  parents,
  articles,
  hcSessionDetails,
}) => {
  React.useEffect(() => {
    if (hcSessionDetails && appSettings) {
      saveSessionDetailsToLocalStorage(appSettings, hcSessionDetails);
    }
  }, [hcSessionDetails, appSettings]);

  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);

  const breadcrumbs = useMemo(() => {
    return [
      { name: t("Help Center", appSettings.language), url: `${baseUrl}` },
      ...parents.map((parent) => ({
        name: parent.title,
        url: `${baseUrl}/categories/${parent.slug}`,
      })),
      { name: category.title },
    ];
  }, [category, parents, baseUrl, appSettings.language]);

  return (
    <StandardLayout
      pageTitle={category.pageTitle || category.title}
      isCustomPageTitle={!!category.pageTitle}
      pageDescription={category.description}
      appSettings={appSettings}
    >
      <Breadcrumbs items={breadcrumbs} />

      <div className={styles.categoryInfo}>
        {category.icon && (
          <div className={styles.categoryInfoIcon}>{category.icon}</div>
        )}
        <h1 className={styles.categoryInfoTitle}>{category.title}</h1>
        {category.description && (
          <div className={styles.categoryInfoDescription}>
            {category.description}
          </div>
        )}
      </div>

      {!articles.length && !subcategories.length && (
        <p className={styles.emptyListMessage}>
          {t("Whoops! Page not found!", appSettings.language)}
        </p>
      )}

      {subcategories.length ? (
        <CategoriesList
          className={styles.categoryPageSubcategories}
          categories={subcategories}
          baseUrl={baseUrl}
          layout={appSettings.layout}
        />
      ) : null}

      {articles.length ? (
        <div className={styles.categoryPageArticles}>
          {subcategories.length ? (
            <h5 className={styles.categoryPageArticlesTitle}>
              {category.title} articles
            </h5>
          ) : null}
          <ArticlesList articles={articles} baseUrl={baseUrl} />
        </div>
      ) : null}
    </StandardLayout>
  );
};

export default CategoryStandardPage;

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
  appSettings: AppSettings & { layout: THelpCenterStandardLayout }
): Promise<ReturnType<GetServerSideProps<TCategoryStandardPageProps>>> => {
  const referer = getDomain(context.req.headers.referer);
  const categorySlug = (context.params?.categorySlug || "").toString();

  const hcSessionDetails = await getSessionDetailsFromRequest(
    context,
    appSettings
  );

  try {
    const allCategories =
      (await loadCategories(appSettings.slug, context)) || [];

    const category = allCategories.find((c) => c.slug === categorySlug);

    if (!category) {
      throw new Error(`Category not found: ${categorySlug}`);
    }

    const subcategories = allCategories.filter(
      (subcategory) => subcategory.parentId === category.id
    );

    const parents: Section[] = [];
    let c = category;
    while (c.parentId) {
      const parent = allCategories.find(
        (category) => category.id === c.parentId
      );
      if (!parent) break;
      parents.unshift(parent);
      c = parent;
    }

    const cookieKey = `atlasIdentity${appSettings.appId}`;
    const currentCustomerId = context.req.cookies[cookieKey];

    const [articles = [], customerData] = await Promise.all([
      loadCategoryArticles(appSettings.slug, categorySlug, context),
      currentCustomerId
        ? loadCustomer(appSettings.appId, currentCustomerId).catch((error) => {
            console.error(error);
            return null;
          })
        : null,
    ]);

    // track the category visit event only once
    trackSessionEventCategoryVisit(
      "VIEWED_CATEGORY",
      hcSessionDetails,
      getPageUrl(appSettings, context),
      [],
      [category],
      { referer }
    );

    if (appSettings) {
      return {
        props: {
          appSettings,
          category,
          subcategories,
          parents,
          articles,
          hcSessionDetails,
          customerData,
        },
      };
    }
  } catch (error: any) {
    if (appSettings.isServedFromCustomDomain) {
      try {
        const object = await getObjectByAlias(
          appSettings.companyId,
          context.resolvedUrl,
          context
        );

        if (object) {
          return {
            redirect: {
              statusCode: 302,
              destination: getRedirectUrl(object.path, context),
            },
          };
        }
      } catch (error: any) {
        // Fall through
      }
    }
  }

  return notFound(appSettings);
};
